/* eslint-disable no-unused-vars */
import { getContacts, addContact, deleteContact, updateContact, getContactPublicKey, inviteContact } from "@/services/contacts.service";
import { shareFile, unshareFile } from "../../services/files.service";

const state = () => ({
    contactList: [],
    isLoading: {
        list: false,
        add: false,
        edit: false,
        delete: false,
        share: false
    },
    error: null
});

const mutations = {
    SET_CONTACT_LIST(state, contactList) {
        state.contactList = Array.isArray(contactList) ? contactList : [];
    },
    ADD_CONTACT(state, contact) {
        state.contactList.push(contact);
    },
    UPDATE_CONTACT(state, updatedContact) {
        const index = state.contactList.findIndex(contact => contact.email === updatedContact.email);
        if (index !== -1) {
            state.contactList[index] = { ...state.contactList[index], ...updatedContact };
        } else {
            console.warn(`Contact with ID ${updatedContact.email} not found.`);
        }
    },
    DELETE_CONTACT(state, contactEmail) {
        state.contactList = state.contactList.filter(contact => contact.email !== contactEmail);
    },
    SET_LOADING(state, { screen, isLoading }) {
        state.isLoading[screen] = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    }
};

const actions = {
    async fetchContactList({ commit, dispatch, state }) {
        if (state.isLoading.list) {
            return;
        }

        commit("SET_LOADING", { screen: "list", isLoading: true });
        commit("SET_ERROR", null);

        try {
            const response = await getContacts();
            const contacts = response.contacts || [];
            
            // Fetch public keys for each contact
            const contactsWithKeys = await Promise.all(
                contacts.map(async (contact) => {
                    try {
                        const keyResponse = await getContactPublicKey(contact.email);
                        return {
                            ...contact,
                            publicKey: keyResponse.publicKey,
                            keyId: keyResponse.keyId,
                            invited: true
                        };
                    } catch (error) {
                        console.warn(`Failed to fetch public key for ${contact.email}:`, error);
                        return {
                            ...contact,
                            invited: false
                        };
                    }
                })
            );

            commit("SET_CONTACT_LIST", Array.isArray(contactsWithKeys) ? contactsWithKeys : []);
            dispatch("analytics/trackEvent", {
                event: "contacts_loaded",
                properties: {
                    count: contactsWithKeys.length
                }
            }, { root: true });
        } catch (error) {
            commit("SET_ERROR", error.message);
            dispatch("snackbar/showSnackbar", { title: "Error loading contacts!", color: "danger" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "contacts_load_failed",
                properties: {
                    error: error.message
                }
            }, { root: true });
        } finally {
            commit("SET_LOADING", { screen: "list", isLoading: false });
        }
    },

    async addContactAction({ commit, dispatch }, contact) {
        commit("SET_LOADING", { screen: "add", isLoading: true });
        commit("SET_ERROR", null);
        try {
            await addContact(contact);
            commit("ADD_CONTACT", contact);
            dispatch("snackbar/showSnackbar", { title: "Contact added successfully!", color: "success" }, { root: true });   
            dispatch("analytics/trackEvent", {
                event: "contact_added",
                properties: {
                    email: contact.email
                }
            }, { root: true });
        } catch (error) {
            commit("SET_ERROR", error.message);
            let errorData;
            try {
                const jsonStr = error.message.split('API request failed: ')[1];
                errorData = JSON.parse(jsonStr);
            } catch (e) {
                errorData = { code: 'UNKNOWN_ERROR', error: error.message };
            }

            if (errorData.code === "CONTACT_ALREADY_EXISTS") {
                dispatch("snackbar/showSnackbar", { title: "A contact with this email already exists", color: "danger" }, { root: true });
            } else {
                dispatch("snackbar/showSnackbar", { title: errorData.error || "Error adding contact!", color: "danger" }, { root: true });
            }
            dispatch("analytics/trackEvent", {
                event: "contact_add_failed",
                properties: {
                    error: errorData.error || error.message
                }
            }, { root: true });
            throw error;
        } finally {
            commit("SET_LOADING", { screen: "add", isLoading: false });
        }
    },

    async inviteContactAction({ commit, dispatch }, contact) {
        commit("SET_LOADING", { screen: "invite", isLoading: true });
        commit("SET_ERROR", null);
        try {
            await inviteContact(contact.email);
            dispatch("snackbar/showSnackbar", { title: "Invite sent successfully!", color: "success" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "contact_invited",
                properties: {
                    email: contact.email
                }
            }, { root: true });
        } catch (error) {
            commit("SET_ERROR", error.message);
            dispatch("snackbar/showSnackbar", { title: "Error inviting contact!", color: "danger" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "contact_invite_failed",
                properties: {
                    error: error.message
                }
            }, { root: true });
        } finally {
            commit("SET_LOADING", { screen: "invite", isLoading: false });
        }
    },

    async removeFileIdFromContactAction({ commit, dispatch }, payload) {
        try {
            await unshareFile(payload.fileId, payload.contact.email);
            dispatch("snackbar/showSnackbar", { title: "File unshared successfully!", color: "success" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "file_unshared",
                properties: {
                    fileId: payload.fileId,
                    contactEmail: payload.contact.email
                }
            }, { root: true });
            // const updatedFileList = (payload.contact.sharedFiles || []).filter(id => id !== payload.fileId);

            // const updatedContact = {
            //     ...payload.contact,
            //     sharedFiles: updatedFileList
            // };

            // await updateContact(updatedContact);
            // commit("UPDATE_CONTACT", updatedContact);
        } catch (error) {
            dispatch("snackbar/showSnackbar", { title: "Error unsharing file!", color: "danger" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "file_unshare_failed",
                properties: {
                    error: error.message
                }
            }, { root: true });
            console.error("Error removing file:", error);
        }
    },

    async updateContactAction({ commit, dispatch }, updatedContact) {
        commit("SET_LOADING", { screen: "edit", isLoading: true });
        commit("SET_ERROR", null);
        try {
            await updateContact(updatedContact);
            commit("UPDATE_CONTACT", updatedContact);
            dispatch("snackbar/showSnackbar", { title: "Contact updated successfully!", color: "success" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "contact_updated",
                properties: {
                    email: updatedContact.email
                }
            }, { root: true });
        } catch (error) {
            commit("SET_ERROR", error.message);
            dispatch("snackbar/showSnackbar", { title: "Error updating contact!", color: "danger" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "contact_update_failed",
                properties: {
                    error: error.message
                }
            }, { root: true });
            throw error;
        } finally {
            commit("SET_LOADING", { screen: "edit", isLoading: false });
        }
    },
    
    async deleteContactAction({ commit, dispatch }, contact) {
        commit("SET_LOADING", { screen: "delete", isLoading: true });
        commit("SET_ERROR", null);
        try {
            await deleteContact(contact.email);
            commit("DELETE_CONTACT", contact.email);
            dispatch("snackbar/showSnackbar", { title: "Contact deleted successfully!", color: "success" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "contact_deleted",
                properties: {
                    email: contact.email
                }
            }, { root: true });
        } catch (error) {
            commit("SET_ERROR", error.message);
            dispatch("snackbar/showSnackbar", { title: "Error deleting contact!", color: "danger" }, { root: true });
            dispatch("analytics/trackEvent", {
                event: "contact_delete_failed",
                properties: {
                    error: error.message
                }
            }, { root: true });
            throw error;
        } finally {
            commit("SET_LOADING", { screen: "delete", isLoading: false });
        }
    },

    /**
     * Clears all contact-related state
     * Used during logout or session end
     */
    clearContactsState({ commit }) {
        console.log('👥 Clearing contacts state...');
        
        // Clear contact list
        commit('SET_CONTACT_LIST', []);
        commit('SET_ERROR', null);
        
        // Clear all loading states
        commit('SET_LOADING', { screen: 'list', isLoading: false });
        commit('SET_LOADING', { screen: 'add', isLoading: false });
        commit('SET_LOADING', { screen: 'edit', isLoading: false });
        commit('SET_LOADING', { screen: 'delete', isLoading: false });
        commit('SET_LOADING', { screen: 'share', isLoading: false });
        
        console.log('✅ Contacts state cleared');
    },

};

const getters = {
    getContactList: (state) => state.contactList,
    isLoadingContacts: (state) => state.isLoading.list,
    isLoadingAddContact: (state) => state.isLoading.add,
    isLoadingEditContact: (state) => state.isLoading.edit,
    isLoadingDeleteContact: (state) => state.isLoading.delete,
    isLoadingShareContact: (state) => state.isLoading.share,
    getContactsByEmails: (state) => (emails) => {
        return state.contactList.filter(contact => emails.includes(contact.email));
    },
    getContactColor: (state) => (email) => {
        const contact = state.contactList.find(contact => contact.email === email);
        return contact ? contact.color : null;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};